import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useT } from "../../context/LanguageContext";
import { toast } from "sonner";
import QuickReplyChips from "../components/QuickReplyChips";
import {
  Loader2, Trophy, ChevronLeft, Upload, Send, Clock, Crown, Users,
  CheckCircle2, Lock, Smartphone, Monitor, MessageCircle, Swords,
} from "lucide-react";

/**
 * V2 — World Cup TOURNAMENT (test mode, 48 players).
 *   /v2/world-cup-tourney/:platform
 */
export default function WCTourneyV2() {
  const { t } = useT();
  const { platform: platformParam } = useParams();
  const platform = platformParam === "console" ? "console" : "mobile";
  const { user } = useAuth();
  const [state, setState] = useState(null);
  const [tab, setTab] = useState("home");

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get(`/wctourney/state?platform=${platform}`);
      setState(data);
    } catch {
      setState({ error: true });
    }
  }, [platform]);
  useEffect(() => { refresh(); }, [refresh]);
  // poll every 15s
  useEffect(() => { const tk = setInterval(refresh, 15_000); return () => clearInterval(tk); }, [refresh]);

  if (!state) return <Spinner />;
  if (state.error) return (
    <div className="text-center text-sm text-[#71717A] py-20" data-testid="v2-wctourney-error">
      {t("wct.unavailable")}
    </div>
  );

  const tn = state.tournament;
  const PlatformIcon = platform === "console" ? Monitor : Smartphone;
  const platformLabel = platform === "console" ? t("wct.platform.console") : t("wct.platform.mobile");

  const ACCENT = "#FFD700"; // Tournament identity (gold)

  // Iter 99 — "Pending" catch-up matches (nations already played IRL when the
  // 48 locked in). Surfaced in their own tab so both players can find & play them.
  const myMatches = state.my_matches || [];
  const pendingMatches = myMatches.filter(
    (m) => m.is_pending && m.status !== "confirmed" && m.status !== "forfeited"
  );

  return (
    <div className="space-y-4 pb-8" data-testid="v2-wctourney-page">
      <header className="space-y-1">
        {/* back (left) */}
        <div className="mb-1">
          <Link to="/v2/wc2026/tournament" className="text-[10px] font-mono uppercase tracking-widest text-white/55 hover:text-white inline-flex items-center gap-1" data-testid="v2-wct-back">
            <ChevronLeft className="w-3.5 h-3.5" /> {t("wct.backChip")}
          </Link>
        </div>

        {/* centered emblem + title + status·platform */}
        <div className="relative flex flex-col items-center text-center pt-1 pb-2">
          <div aria-hidden className="absolute top-0 left-1/2 -translate-x-1/2 w-64 h-40 rounded-full blur-[90px] pointer-events-none" style={{ background: `${ACCENT}1f` }} />
          <div className="relative mb-2.5">
            <div aria-hidden className="absolute inset-0 -m-4 rounded-full blur-2xl" style={{ background: `${ACCENT}33` }} />
            <img src="/wc/wc2026-dark.png" alt="FIFA World Cup 2026" className="relative h-16 sm:h-20 w-auto object-contain drop-shadow-[0_4px_16px_rgba(0,0,0,0.6)]" draggable={false} />
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl uppercase tracking-wider leading-none bg-clip-text text-transparent" style={{ backgroundImage: `linear-gradient(90deg, #ffffff 0%, ${ACCENT} 140%)` }}>
            {t("wct.title")}
          </h1>
          <div className="text-[10px] font-mono uppercase tracking-[0.35em] text-white/45 mt-2">
            01 · Authentic · FIFA World Cup 2026
          </div>
          <div className="mt-3 flex items-center justify-center gap-2">
            {(() => {
              const S = {
                forming:     { label: t("wct.tracker.title"),  color: "#FBBF24", pulse: true },
                group_stage: { label: t("wct.tabs.group"),     color: "#22C55E", pulse: true },
                knockout:    { label: t("wct.tabs.bracket"),   color: "#22C55E", pulse: true },
                done:        { label: t("wct.tracker.complete"), color: "#71717A", pulse: false },
              }[tn?.status] || { label: tn?.status || "—", color: ACCENT, pulse: false };
              return (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full" style={{ background: `${S.color}1f`, border: `1px solid ${S.color}55`, color: S.color }}>
                  <span className={`w-1.5 h-1.5 rounded-full ${S.pulse ? "animate-pulse" : ""}`} style={{ background: S.color }} />
                  <span className="text-[9px] font-extrabold uppercase tracking-[0.3em]">{S.label}</span>
                </span>
              );
            })()}
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/5 border border-white/10">
              <PlatformIcon className="w-3 h-3" style={{ color: ACCENT }} />
              <span className="text-[9px] font-mono uppercase tracking-widest text-white/75">{platformLabel}</span>
            </span>
          </div>
        </div>
      </header>

      <div className="flex gap-2" data-testid="v2-wct-platform-switcher">
        {[["console", t("wct.platform.console"), Monitor], ["mobile", t("wct.platform.mobile"), Smartphone]].map(([p, label, Icon]) => (
          <Link key={p} to={`/v2/world-cup-tourney/${p}`} data-testid={`v2-wct-platbtn-${p}`}
            className={`flex-1 inline-flex items-center justify-center gap-1.5 py-2 rounded-full text-[11px] font-extrabold uppercase tracking-widest transition ${platform === p ? "bg-[#FFD700] text-[#0F172A] shadow-lg" : "bg-white/5 text-white/70 hover:bg-white/10"}`}>
            <Icon className="w-3.5 h-3.5" /> {label}
          </Link>
        ))}
      </div>

      {/* Registration tracker — always visible (before, during, after join) */}
      {tn && <RegistrationTracker t={tn} />}

      {/* Groups preview — always visible while tournament is forming or in
          group stage, so people can see who has drawn into which group. */}
      {tn && (tn.status === "forming" || tn.status === "group_stage" || tn.status === "knockout") && (
        <GroupsPreview t={tn} playersMeta={state.players_meta || {}} myId={user?.id} myGroup={state.my_group} />
      )}

      {/* Not joined */}
      {!state.in_tournament && tn && tn.status === "forming" && (
        <JoinPanel t={tn} platform={platform} refresh={refresh} />
      )}
      {!state.in_tournament && (!tn || tn.status !== "forming") && (
        <div className="rounded-2xl bg-[#12121C] border border-[#1F1F2A] p-6 text-center text-xs text-[#71717A]">
          {t("wct.idleBody")}
        </div>
      )}

      {/* Joined */}
      {state.in_tournament && tn && (
        <>
          <MySlotCard t={tn} myTeam={state.my_team} myGroup={state.my_group} />
          <div className="flex gap-1.5 overflow-x-auto no-scrollbar" data-testid="v2-wct-tabs">
            {[
              ["home", t("wct.tabs.matches"), Trophy],
              ...(pendingMatches.length > 0 ? [["pending", `Pending · ${pendingMatches.length}`, Clock]] : []),
              ["group", t("wct.tabs.group"), Users],
              ["standings", t("wct.tabs.standings"), Crown],
              ...(tn.status === "knockout" || tn.status === "done" ? [["bracket", t("wct.tabs.bracket"), Trophy]] : []),
            ].map(([id, label, Icon]) => (
              <button key={id} type="button" onClick={() => setTab(id)} data-testid={`v2-wct-tab-${id}`}
                className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-extrabold uppercase tracking-widest ${tab === id ? "bg-[#FFD700] text-[#0F172A]" : "bg-white/5 text-white/70 hover:bg-white/10"}`}>
                <Icon className="w-3 h-3" /> {label}
              </button>
            ))}
          </div>

          {tab === "home" && <MyMatches matches={state.my_matches} myId={user?.id} refresh={refresh} platform={platform} />}
          {tab === "pending" && <PendingMatches matches={myMatches} myId={user?.id} refresh={refresh} platform={platform} playersMeta={state.players_meta || {}} />}
          {tab === "group" && <GroupView tournamentId={tn.id} group={state.my_group} myId={user?.id} t={tn} />}
          {tab === "standings" && <Standings tournamentId={tn.id} highlightGroup={state.my_group} />}
          {tab === "bracket" && <Bracket tournamentId={tn.id} myId={user?.id} />}
        </>
      )}
    </div>
  );
}

function Spinner() {
  return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[#FFD700]" /></div>;
}

// Flag pool for the join slot-machine animation
const TOURNEY_FLAG_POOL = ["🇲🇽","🇧🇷","🇪🇸","🇦🇷","🇫🇷","🇩🇪","🇵🇹","🇳🇱","🏴󠁧󠁢󠁥󠁮󠁧󠁿","🇯🇵","🇲🇦","🇸🇦","🇺🇸","🇧🇪","🇭🇷","🇺🇾","🇨🇴","🇸🇳","🇨🇭","🇩🇿"];

function JoinPanel({ t: tn, platform, refresh }) {
  const { t } = useT();
  const [busy, setBusy] = useState(false);
  const [reveal, setReveal] = useState(null); // { team, group }
  const join = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/wctourney/join", { platform });
      // iter 97 — the team is only returned once the admin opens the team
      // reveal; until then it's null, so skip the slot-machine reveal.
      if (data.team && data.team.name) {
        setReveal({ team: data.team, group: data.group });
      } else {
        toast.success("You're in! Your team is revealed when the admin opens it.");
        setBusy(false);
        await refresh();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("wct.join.fail"));
      setBusy(false);
    }
  };
  return (
    <div className="rounded-2xl bg-gradient-to-br from-[#FFD700]/15 to-[#1E3A8A]/15 border border-[#FFD700]/40 p-5 text-center space-y-3" data-testid="v2-wct-join-panel">
      <Trophy className="w-10 h-10 text-[#FFD700] mx-auto" />
      <div>
        <div className="text-xs font-mono uppercase tracking-widest text-[#FFD700]">{t("wct.join.slots")}</div>
        <div className="text-4xl font-extrabold text-white tabular-nums mt-1">{tn.filled}/48</div>
      </div>
      <div className="h-2 rounded-full bg-white/10 overflow-hidden">
        <div className="h-full bg-[#FFD700]" style={{ width: `${(tn.filled / 48) * 100}%` }} />
      </div>
      <button type="button" onClick={join} disabled={busy} data-testid="v2-wct-join-btn"
        className="w-full inline-flex items-center justify-center gap-1.5 py-2.5 rounded-full bg-[#FFD700] text-[#0F172A] text-xs font-extrabold uppercase tracking-widest shadow disabled:opacity-50">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trophy className="w-4 h-4" />}
        {t("wct.join.cta")}
      </button>
      <p className="text-[10px] text-white/60 leading-snug">
        {t("wct.join.body")}
      </p>
      {reveal && (
        <TourneyReveal
          team={reveal.team}
          group={reveal.group}
          onDone={async () => { setReveal(null); setBusy(false); await refresh(); }}
        />
      )}
    </div>
  );
}

/* Join reveal — flag slot-machine → your nation → your group */
function TourneyReveal({ team, group, onDone }) {
  const { t } = useT();
  const ACCENT = "#FFD700";
  const [phase, setPhase] = useState("rolling"); // rolling → team → group
  const [spin, setSpin] = useState(0);
  useEffect(() => {
    if (phase !== "rolling") return undefined;
    const tk = setInterval(() => setSpin((i) => i + 1), 70);
    return () => clearInterval(tk);
  }, [phase]);
  useEffect(() => {
    const timers = [
      setTimeout(() => setPhase("team"), 1400),
      setTimeout(() => setPhase("group"), 2900),
    ];
    return () => timers.forEach(clearTimeout);
  }, []);
  const spinFlag = TOURNEY_FLAG_POOL[spin % TOURNEY_FLAG_POOL.length];
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#020617]/95 backdrop-blur-md" data-testid="v2-wct-reveal">
      <div aria-hidden className="absolute inset-0" style={{ background: `radial-gradient(120% 80% at 50% 30%, ${ACCENT}1f 0%, transparent 60%)` }} />
      <div className="relative w-full max-w-sm text-center">
        {phase === "rolling" && (
          <div data-testid="v2-wct-reveal-rolling">
            <div className="text-[10px] font-mono uppercase tracking-[0.4em] mb-6" style={{ color: ACCENT }}>{t("wc26.league.reveal.drawing")}</div>
            <div className="relative mx-auto w-40 h-40 rounded-3xl flex items-center justify-center overflow-hidden" style={{ background: `${ACCENT}10`, border: `1.5px solid ${ACCENT}40` }}>
              <div aria-hidden className="absolute inset-0 -m-6 blur-2xl rounded-full" style={{ background: `${ACCENT}30` }} />
              <span className="relative text-7xl animate-pulse">{spinFlag}</span>
            </div>
            <Loader2 className="w-5 h-5 mx-auto mt-5 animate-spin" style={{ color: ACCENT }} />
          </div>
        )}
        {phase === "team" && (
          <div data-testid="v2-wct-reveal-team" className="animate-[fadeIn_.3s_ease]">
            <div className="text-[10px] font-mono uppercase tracking-[0.4em] mb-5" style={{ color: ACCENT }}>{t("wct.yourTeam")}</div>
            <div className="relative mx-auto w-44 h-44 rounded-3xl flex items-center justify-center" style={{ background: `${ACCENT}14`, border: `2px solid ${ACCENT}` }}>
              <div aria-hidden className="absolute inset-0 -m-8 blur-3xl rounded-full" style={{ background: `${ACCENT}45` }} />
              <span className="relative text-8xl drop-shadow-2xl">{team?.flag}</span>
            </div>
            <div className="font-heading text-3xl uppercase tracking-wider mt-5">{team?.name}</div>
          </div>
        )}
        {phase === "group" && (
          <div data-testid="v2-wct-reveal-group" className="animate-[fadeIn_.3s_ease]">
            <div className="text-5xl mb-3">{team?.flag}</div>
            <div className="font-heading text-2xl uppercase tracking-wider">{team?.name}</div>
            <div className="mt-4 inline-block px-5 py-2 rounded-2xl" style={{ background: `${ACCENT}1a`, border: `1.5px solid ${ACCENT}` }}>
              <span className="font-heading text-2xl uppercase tracking-wider" style={{ color: ACCENT }}>{t("wct.group", { g: group })}</span>
            </div>
            <button
              type="button"
              onClick={onDone}
              data-testid="v2-wct-reveal-continue"
              className="mt-7 w-full inline-flex items-center justify-center gap-2 rounded-xl bg-[#FFD700] text-[#020617] font-extrabold px-5 py-3 text-sm uppercase tracking-wider hover:translate-y-[-1px] transition-transform"
            >
              {t("wc26.league.reveal.continue")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function MySlotCard({ t: tn, myTeam, myGroup }) {
  const { t } = useT();
  if (!myTeam) return null;
  return (
    <div className="rounded-2xl bg-gradient-to-br from-[#FFD700]/10 to-[#1E3A8A]/10 border border-[#FFD700]/30 p-4 flex items-center gap-3" data-testid="v2-wct-my-slot">
      <div className="text-4xl">{myTeam.flag}</div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] font-mono uppercase tracking-widest text-[#FFD700]">{t("wct.yourTeam")}</div>
        <div className="text-sm font-bold text-white">{myTeam.name}</div>
        <div className="text-[10px] text-[#71717A] font-mono">{t("wct.group", { g: myGroup })} · {tn.status.replace(/_/g, " ")}</div>
      </div>
      {tn.stage_deadline && tn.status === "group_stage" && (
        <DeadlineBadge deadline={tn.stage_deadline} />
      )}
    </div>
  );
}

function DeadlineBadge({ deadline }) {
  const { t } = useT();
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const tk = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(tk); }, []);
  const secs = Math.max(0, Math.floor((new Date(deadline).getTime() - now) / 1000));
  const d = Math.floor(secs / 86400);
  const h = Math.floor((secs % 86400) / 3600);
  const m = Math.floor((secs % 3600) / 60);
  return (
    <div className="text-right" data-testid="v2-wct-deadline">
      <div className="text-[9px] font-mono uppercase tracking-widest text-[#71717A]">{t("wct.deadline.ends")}</div>
      <div className="text-sm font-mono font-bold text-white tabular-nums">{d}d {String(h).padStart(2,"0")}h {String(m).padStart(2,"0")}m</div>
    </div>
  );
}

function MyMatches({ matches, myId, refresh, platform }) {
  const { t } = useT();
  if (!matches || matches.length === 0) {
    return <div className="rounded-2xl bg-[#12121C] border border-[#1F1F2A] p-6 text-center text-xs text-[#71717A]">{t("wct.noMatches")}</div>;
  }
  return (
    <div className="space-y-2" data-testid="v2-wct-my-matches">
      {matches.map(m => <MatchRow key={m.id} m={m} myId={myId} refresh={refresh} platform={platform} />)}
    </div>
  );
}

/* Iter 99 — Pending (catch-up) matches tab. Lists the group fixtures whose
   nations already played IRL when the 48 locked in. Each row opens the SAME
   chat + score + screenshot flow (MatchRow). A shared 48h countdown drives the
   auto-resolution handled by the backend `_sweep_pending_matches`. */
function PendingMatches({ matches, myId, refresh, platform, playersMeta }) {
  const pending = (matches || []).filter(
    (m) => m.is_pending && m.status !== "confirmed" && m.status !== "forfeited"
  );
  const resolved = (matches || []).filter(
    (m) => m.is_pending && (m.status === "confirmed" || m.status === "forfeited")
  );
  if (pending.length === 0 && resolved.length === 0) {
    return (
      <div className="rounded-2xl bg-[#12121C] border border-[#1F1F2A] p-6 text-center text-xs text-[#71717A]" data-testid="v2-wct-pending-empty">
        No catch-up matches — your nations haven't played their real-world games yet.
      </div>
    );
  }
  const deadline = pending[0]?.deadline;
  return (
    <div className="space-y-2" data-testid="v2-wct-pending">
      <div className="rounded-2xl border border-[#FFD700]/30 bg-[#FFD700]/[0.06] p-3">
        <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-[#FFD700]">
          <Clock className="w-3.5 h-3.5" /> Pending catch-up matches
        </div>
        <p className="text-[11px] text-white/70 mt-1 leading-snug">
          These nations already played in real life. Open a match to chat, agree a time, then submit the score + screenshot — same as any match. If it isn't played in time it auto-resolves.
        </p>
        {deadline && <PendingCountdown deadline={deadline} />}
      </div>
      {pending.map((m) => (
        <MatchRow key={m.id} m={m} myId={myId} refresh={refresh} platform={platform} playersMeta={playersMeta} />
      ))}
      {resolved.length > 0 && (
        <>
          <div className="text-[10px] font-mono uppercase tracking-widest text-white/40 pt-2 px-1">Resolved</div>
          {resolved.map((m) => (
            <MatchRow key={m.id} m={m} myId={myId} refresh={refresh} platform={platform} playersMeta={playersMeta} />
          ))}
        </>
      )}
    </div>
  );
}

function PendingCountdown({ deadline }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const end = new Date(deadline).getTime();
  if (!Number.isFinite(end)) return null;
  const ms = end - now;
  if (ms <= 0) {
    return (
      <div className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-mono font-bold text-rose-300" data-testid="v2-wct-pending-countdown">
        <Clock className="w-3.5 h-3.5" /> Window closed — auto-resolving…
      </div>
    );
  }
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const mm = Math.floor((totalSec % 3600) / 60);
  const ss = totalSec % 60;
  return (
    <div className="mt-2 inline-flex items-center gap-1.5 text-[12px] font-mono font-bold text-[#FFD700] tabular-nums" data-testid="v2-wct-pending-countdown">
      <Clock className="w-3.5 h-3.5" /> {h}h {String(mm).padStart(2, "0")}m {String(ss).padStart(2, "0")}s left to play
    </div>
  );
}

function MatchRow({ m, myId, refresh, platform: _platform, playersMeta }) {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const isP1 = m.p1_id === myId;
  const myTeam = isP1 ? m.p1_team : m.p2_team;
  const oppTeam = isP1 ? m.p2_team : m.p1_team;
  const mySub = isP1 ? m.p1_submission : m.p2_submission;
  const myScore = isP1 ? m.confirmed_score_p1 : m.confirmed_score_p2;
  const oppScore = isP1 ? m.confirmed_score_p2 : m.confirmed_score_p1;
  const isHost = m.host_id === myId;
  const stageLabel = m.stage === "group" ? t("wct.group", { g: m.group }) : m.round;
  const settled = m.status === "confirmed" || m.status === "forfeited";
  // iter 99 — show the two PLAYER names (X vs Y) when player meta is available
  // (used by the Pending tab). "Me" labels the current player's side.
  const myName = playersMeta?.[myId]?.username;
  const oppId = isP1 ? m.p2_id : m.p1_id;
  const oppName = playersMeta?.[oppId]?.username;

  return (
    <div className="rounded-2xl bg-[#12121C] border border-[#1F1F2A] overflow-hidden" data-testid={`v2-wct-match-${m.id}`}>
      <button type="button" onClick={() => setOpen(v => !v)} className="w-full p-3 flex items-center gap-3 hover:bg-white/[0.02]">
        <div className="text-2xl">{myTeam?.flag}</div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-[#71717A]">vs</div>
        <div className="text-2xl">{oppTeam?.flag}</div>
        <div className="flex-1 min-w-0 text-left">
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#FFD700]">{stageLabel}</div>
          {playersMeta && (myName || oppName) && (
            <div className="text-[12px] font-bold text-white truncate" data-testid={`v2-wct-match-names-${m.id}`}>
              @{myName || "you"} <span className="text-white/40">vs</span> @{oppName || "opponent"}
            </div>
          )}
          <div className="text-xs text-white/70 truncate">{myTeam?.name} <span className="text-white/40">vs</span> {oppTeam?.name}</div>
        </div>
        {settled ? (
          <div className={`text-sm font-extrabold ${myScore > oppScore ? "text-emerald-300" : myScore < oppScore ? "text-red-300" : "text-white/70"}`}>
            {myScore}-{oppScore}
          </div>
        ) : (
          <div className="text-[10px] uppercase font-bold text-[#FFD700]">
            {isHost ? t("wct.match.host") : t("wct.match.guest")}
          </div>
        )}
      </button>
      {open && !settled && !m.needs_extra_time && (
        <SubmitPanel m={m} myId={myId} refresh={refresh} mySubmitted={!!mySub} bothSubmitted={!!(m.p1_submission && m.p2_submission)} />
      )}
      {open && m.needs_extra_time && (
        <ExtraTimePanel m={m} myId={myId} refresh={refresh} />
      )}
      {open && settled && m.forfeit_reason && (
        <div className="border-t border-[#1F1F2A] px-3 py-2 text-[10px] text-amber-200">
          {m.forfeit_reason === "chat_silence" && t("wct.match.forfSilence")}
          {m.forfeit_reason === "deadline_missed" && t("wct.match.forfDeadline")}
        </div>
      )}
      {open && <MatchChat matchId={m.id} myId={myId} />}
    </div>
  );
}

function SubmitPanel({ m, myId, refresh, mySubmitted, bothSubmitted }) {
  const { t } = useT();
  const [my, setMy] = useState("");
  const [opp, setOpp] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (my === "" || opp === "") { toast.error(t("wct.submit.scores")); return; }
    if (!file) { toast.error(t("wct.submit.shotReq")); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("my_score", String(my));
      fd.append("opp_score", String(opp));
      fd.append("screenshot", file);
      await api.post(`/wctourney/matches/${m.id}/submit`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(t("wct.submit.toast"));
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || t("wct.submit.fail")); }
    finally { setBusy(false); }
  };
  return (
    <div className="border-t border-[#1F1F2A] p-3 space-y-2.5" data-testid={`v2-wct-submit-${m.id}`}>
      {!mySubmitted && (
        <>
          <div className="flex gap-2">
            <input type="number" min={0} max={99} value={my} onChange={e => setMy(e.target.value)} placeholder={t("wct.submit.phMy")} style={{ fontSize: "16px" }} data-testid={`v2-wct-myscore-${m.id}`}
              className="flex-1 px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-center text-lg font-bold text-white" />
            <input type="number" min={0} max={99} value={opp} onChange={e => setOpp(e.target.value)} placeholder={t("wct.submit.phOpp")} style={{ fontSize: "16px" }} data-testid={`v2-wct-oppscore-${m.id}`}
              className="flex-1 px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-center text-lg font-bold text-white" />
          </div>
          <input type="file" accept="image/*" onChange={e => setFile(e.target.files?.[0] || null)} data-testid={`v2-wct-screenshot-${m.id}`}
            className="block w-full text-[10px] text-white file:mr-2 file:py-1 file:px-2 file:rounded-full file:border-0 file:text-[9px] file:font-bold file:uppercase file:bg-[#FFD700] file:text-[#0F172A]" />
          <button type="button" onClick={submit} disabled={busy} data-testid={`v2-wct-submitbtn-${m.id}`}
            className="w-full inline-flex items-center justify-center gap-1.5 py-2 rounded-full bg-[#FFD700] text-[#0F172A] text-[10px] font-extrabold uppercase tracking-widest disabled:opacity-50">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
            {t("wct.submit.cta")}
          </button>
        </>
      )}
      {mySubmitted && !bothSubmitted && (
        <div className="text-center text-[11px] text-emerald-300">
          <CheckCircle2 className="w-4 h-4 mx-auto" /> {t("wct.submit.waiting")}
        </div>
      )}
      <AutoConfirmHint m={m} mySubmitted={mySubmitted} bothSubmitted={bothSubmitted} />
    </div>
  );
}

// FIX 2 — Extra-time / penalties form for drawn knockout matches.
// Appears when the backend flags a KO match (single-leg F/3RD that ended
// level, or a two-leg tie level after aggregate + away goals) with
// `needs_extra_time`. POSTs to /wctourney/matches/{id}/extra-time with the
// exact payload the handler `_h_submit_extra_time` expects:
//   my_et, opp_et (required, 0-99) and optional my_pens, opp_pens.
// Mirrors SubmitPanel's styling / busy-state / toast patterns.
function ExtraTimePanel({ m, myId, refresh }) {
  const { t } = useT();
  const [myEt, setMyEt] = useState("");
  const [oppEt, setOppEt] = useState("");
  const [myPens, setMyPens] = useState("");
  const [oppPens, setOppPens] = useState("");
  const [busy, setBusy] = useState(false);
  // Penalties only matter when ET itself ends level — surface the inputs then.
  const etLevel = myEt !== "" && oppEt !== "" && Number(myEt) === Number(oppEt);
  const submit = async () => {
    if (myEt === "" || oppEt === "") { toast.error(t("wct.submit.scores")); return; }
    if (etLevel && (myPens === "" || oppPens === "")) { toast.error(t("wct.submit.scores")); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("my_et", String(myEt));
      fd.append("opp_et", String(oppEt));
      if (myPens !== "" && oppPens !== "") {
        fd.append("my_pens", String(myPens));
        fd.append("opp_pens", String(oppPens));
      }
      await api.post(`/wctourney/matches/${m.id}/extra-time`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(t("wct.submit.toast"));
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || t("wct.submit.fail")); }
    finally { setBusy(false); }
  };
  return (
    <div className="border-t border-[#1F1F2A] p-3 space-y-2.5" data-testid={`v2-wct-et-${m.id}`}>
      <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-[#FFD700]">
        <Swords className="w-3.5 h-3.5" /> {t("wct.et.title")}
      </div>
      <div className="text-[10px] text-[#A1A1AA] leading-snug">
        {t("wct.et.body")}
      </div>
      <div className="flex gap-2">
        <input type="number" min={0} max={99} value={myEt} onChange={e => setMyEt(e.target.value)} placeholder={t("wct.submit.phMy")} style={{ fontSize: "16px" }} data-testid={`v2-wct-myet-${m.id}`}
          className="flex-1 px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-center text-lg font-bold text-white" />
        <input type="number" min={0} max={99} value={oppEt} onChange={e => setOppEt(e.target.value)} placeholder={t("wct.submit.phOpp")} style={{ fontSize: "16px" }} data-testid={`v2-wct-oppet-${m.id}`}
          className="flex-1 px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-center text-lg font-bold text-white" />
      </div>
      {etLevel && (
        <>
          <div className="text-[10px] font-bold uppercase tracking-widest text-[#FFD700]">{t("wct.et.pens")}</div>
          <div className="flex gap-2">
            <input type="number" min={0} max={99} value={myPens} onChange={e => setMyPens(e.target.value)} placeholder={t("wct.submit.phMy")} style={{ fontSize: "16px" }} data-testid={`v2-wct-mypens-${m.id}`}
              className="flex-1 px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-center text-lg font-bold text-white" />
            <input type="number" min={0} max={99} value={oppPens} onChange={e => setOppPens(e.target.value)} placeholder={t("wct.submit.phOpp")} style={{ fontSize: "16px" }} data-testid={`v2-wct-opppens-${m.id}`}
              className="flex-1 px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-center text-lg font-bold text-white" />
          </div>
        </>
      )}
      <button type="button" onClick={submit} disabled={busy} data-testid={`v2-wct-etbtn-${m.id}`}
        className="w-full inline-flex items-center justify-center gap-1.5 py-2 rounded-full bg-[#FFD700] text-[#0F172A] text-[10px] font-extrabold uppercase tracking-widest disabled:opacity-50">
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Swords className="w-3.5 h-3.5" />}
        {t("wct.et.cta")}
      </button>
    </div>
  );
}

function AutoConfirmHint({ m, mySubmitted, bothSubmitted }) {
  const { t } = useT();
  const someoneSubmitted = !!(m.p1_submission || m.p2_submission);
  return (
    <div className={`rounded-lg border p-2 text-[10px] leading-snug ${someoneSubmitted && !bothSubmitted ? "border-amber-400/40 bg-amber-500/10 text-amber-100" : "border-[#FFD700]/25 bg-[#FFD700]/5 text-[#FFE99A]"}`} data-testid="v2-wct-auto-hint">
      <div className="flex items-start gap-1.5">
        <Clock className="w-3 h-3 flex-shrink-0 mt-0.5" />
        <div>
          {t("wct.auto.body")} <b>{t("wct.auto.deadline")}</b>. {t("wct.auto.body2")}
          {someoneSubmitted && !bothSubmitted && (
            <div className="mt-1 font-mono tabular-nums font-bold opacity-75">
              {mySubmitted ? t("wct.auto.waiting") : t("wct.auto.yourTurn")}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function MatchChat({ matchId, myId }) {
  const { t } = useT();
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/wctourney/matches/${matchId}/chat`);
      setMsgs(data.messages || []);
    } catch {}
  }, [matchId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { const tk = setInterval(load, 7000); return () => clearInterval(tk); }, [load]);
  const sendText = async (txt) => {
    if (!txt || !txt.trim()) return;
    try {
      await api.post(`/wctourney/matches/${matchId}/chat`, { content: txt.trim() });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("wct.submit.fail"));
      throw e;
    }
  };
  const send = async () => {
    if (!text.trim()) return;
    try {
      await sendText(text);
      setText("");
    } catch { /* error already toasted */ }
  };
  return (
    <div className="border-t border-[#1F1F2A] p-3 space-y-2" data-testid={`v2-wct-chat-${matchId}`}>
      <div className="text-[9px] font-mono uppercase tracking-widest text-[#FFD700] mb-1 flex items-center gap-1"><MessageCircle className="w-3 h-3" /> {t("wct.chat.title")}</div>
      <div className="max-h-32 overflow-y-auto space-y-1 mb-2">
        {msgs.length === 0 && <div className="text-[10px] text-[#71717A] text-center py-1">{t("wct.chat.empty")}</div>}
        {msgs.map(c => (
          <div key={c.id} className={`flex ${c.user_id === myId ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[80%] px-2 py-1 rounded-xl text-[11px] ${c.user_id === myId ? "bg-[#FFD700] text-[#0F172A]" : "bg-white/10 text-white"}`}>{c.content}</div>
          </div>
        ))}
      </div>
      <QuickReplyChips onSend={sendText} testIdPrefix={`v2-wct-quick-${matchId}`} />
      <div className="flex gap-1">
        <input type="text" value={text} onChange={e => setText(e.target.value)} onKeyDown={e => e.key === "Enter" && send()} placeholder={t("wct.chat.placeholder")} style={{ fontSize: "16px" }} className="flex-1 px-2 py-1 rounded bg-black/40 border border-white/10 text-[11px] text-white" />
        <button onClick={send} className="px-2 py-1 rounded bg-[#FFD700] text-[#0F172A] text-[10px] font-bold"><Send className="w-3.5 h-3.5" /></button>
      </div>
    </div>
  );
}

function GroupView({ tournamentId, group, myId, t: _t }) {
  const { t } = useT();
  const [stand, setStand] = useState(null);
  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await api.get(`/wctourney/tournaments/${tournamentId}/standings`);
        if (!cancel) setStand(data.standings?.[group] || []);
      } catch { if (!cancel) setStand([]); }
    })();
    return () => { cancel = true; };
  }, [tournamentId, group]);
  return (
    <div className="space-y-2" data-testid="v2-wct-group-view">
      <div className="rounded-2xl bg-[#12121C] border border-[#1F1F2A] overflow-hidden">
        <div className="px-3 py-2 text-[10px] font-mono uppercase tracking-widest text-[#FFD700] border-b border-[#1F1F2A]">{t("wct.group", { g: group })}</div>
        {stand === null ? <Spinner /> : (
          <div className="divide-y divide-white/5">
            {stand.map(r => (
              <div key={r.user_id} className={`flex items-center gap-2 px-3 py-2 ${r.user_id === myId ? "bg-[#FFD700]/10" : ""}`}>
                <div className="w-5 text-center text-[10px] font-mono text-white/60">#{r.pos}</div>
                <div className="text-xl">{r.team?.flag}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-bold text-white truncate">@{r.username || r.user_id.slice(0,6)}</div>
                  <div className="text-[10px] text-[#71717A] font-mono">{r.played}P · {r.wins}W {r.draws}D {r.losses}L · GD {r.gd>=0?"+":""}{r.gd}</div>
                </div>
                <div className="text-sm font-extrabold text-[#FFD700]">{r.points}</div>
              </div>
            ))}
          </div>
        )}
      </div>
      <GroupChat tournamentId={tournamentId} group={group} myId={myId} />
    </div>
  );
}

function GroupChat({ tournamentId, group, myId }) {
  const { t } = useT();
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/wctourney/tournaments/${tournamentId}/groups/${group}/chat`);
      setMsgs(data.messages || []);
    } catch {}
  }, [tournamentId, group]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { const tk = setInterval(load, 8000); return () => clearInterval(tk); }, [load]);
  const sendText = async (txt) => {
    if (!txt || !txt.trim()) return;
    try {
      await api.post(`/wctourney/tournaments/${tournamentId}/groups/${group}/chat`, { content: txt.trim() });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("wct.submit.fail"));
      throw e;
    }
  };
  const send = async () => {
    if (!text.trim()) return;
    try {
      await sendText(text);
      setText("");
    } catch { /* error already toasted */ }
  };
  return (
    <div className="rounded-2xl bg-[#12121C] border border-[#1F1F2A] p-3 space-y-2" data-testid="v2-wct-group-chat">
      <div className="text-[10px] font-mono uppercase tracking-widest text-[#FFD700] mb-2 flex items-center gap-1"><Users className="w-3 h-3" /> {t("wct.groupChat.title", { g: group })}</div>
      <div className="max-h-40 overflow-y-auto space-y-1.5 mb-2">
        {msgs.length === 0 && <div className="text-[11px] text-[#71717A] text-center py-2">{t("wct.groupChat.empty")}</div>}
        {msgs.map(c => (
          <div key={c.id} className={`flex ${c.user_id === myId ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[80%] px-2 py-1 rounded-xl text-[11px] ${c.user_id === myId ? "bg-[#FFD700] text-[#0F172A]" : "bg-white/10 text-white"}`}>
              <div className="text-[8px] opacity-70">{c.username}</div>
              {c.content}
            </div>
          </div>
        ))}
      </div>
      <QuickReplyChips onSend={sendText} testIdPrefix={`v2-wct-group-quick-${group}`} />
      <div className="flex gap-1">
        <input type="text" value={text} onChange={e => setText(e.target.value)} onKeyDown={e => e.key === "Enter" && send()} placeholder={t("wct.groupChat.placeholder")} style={{ fontSize: "16px" }} className="flex-1 px-2 py-1.5 rounded bg-black/40 border border-white/10 text-[11px] text-white" />
        <button onClick={send} className="px-3 py-1.5 rounded bg-[#FFD700] text-[#0F172A] text-[10px] font-bold"><Send className="w-3.5 h-3.5" /></button>
      </div>
    </div>
  );
}

function Standings({ tournamentId, highlightGroup }) {
  const { t } = useT();
  const [data, setData] = useState(null);
  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await api.get(`/wctourney/tournaments/${tournamentId}/standings`);
        if (!cancel) setData(data.standings || {});
      } catch { if (!cancel) setData({}); }
    })();
    return () => { cancel = true; };
  }, [tournamentId]);
  if (!data) return <Spinner />;
  return (
    <div className="space-y-2" data-testid="v2-wct-standings">
      {Object.entries(data).map(([g, rows]) => (
        <div key={g} className={`rounded-2xl bg-[#12121C] border overflow-hidden ${g === highlightGroup ? "border-[#FFD700]" : "border-[#1F1F2A]"}`}>
          <div className="px-3 py-2 text-[10px] font-mono uppercase tracking-widest text-[#FFD700] border-b border-[#1F1F2A]">{t("wct.group", { g })}</div>
          <div className="divide-y divide-white/5">
            {rows.map(r => (
              <div key={r.user_id} className="flex items-center gap-2 px-3 py-1.5">
                <div className="w-5 text-center text-[10px] font-mono text-white/60">#{r.pos}</div>
                <div className="text-lg">{r.team?.flag}</div>
                <div className="flex-1 min-w-0 text-xs font-bold text-white truncate">@{r.username || r.user_id.slice(0,6)}</div>
                <div className="text-[10px] font-mono text-[#71717A]">{r.played}P {r.wins}W {r.draws}D {r.losses}L</div>
                <div className="text-sm font-extrabold text-[#FFD700] w-6 text-right">{r.points}</div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function Bracket({ tournamentId, myId }) {
  const { t } = useT();
  const [matches, setMatches] = useState(null);
  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await api.get(`/wctourney/tournaments/${tournamentId}/bracket`);
        if (!cancel) setMatches(data.matches || []);
      } catch { if (!cancel) setMatches([]); }
    })();
    return () => { cancel = true; };
  }, [tournamentId]);
  if (!matches) return <Spinner />;
  const byRound = matches.reduce((acc, m) => { (acc[m.round] = acc[m.round] || []).push(m); return acc; }, {});
  const order = ["R32", "R16", "QF", "SF", "3RD", "F"];
  return (
    <div className="space-y-3" data-testid="v2-wct-bracket">
      {order.filter(r => byRound[r]).map(r => (
        <div key={r} className="rounded-2xl bg-[#12121C] border border-[#1F1F2A] overflow-hidden">
          <div className="px-3 py-2 text-[10px] font-mono uppercase tracking-widest text-[#FFD700] border-b border-[#1F1F2A]">{r === "F" ? t("wct.bracket.final") : r === "3RD" ? t("wct.bracket.third") : r}</div>
          <div className="divide-y divide-white/5">
            {(byRound[r] || []).sort((a,b) => (a.bracket_pos || 0) - (b.bracket_pos || 0)).map(m => (
              <div key={m.id} className={`flex items-center gap-2 px-3 py-2 text-xs ${m.p1_id === myId || m.p2_id === myId ? "bg-[#FFD700]/10" : ""}`}>
                <div className="flex-1 min-w-0">
                  <BracketSide team={m.p1_team} user={m.p1_user} winner={m.winner_id === m.p1_id} />
                  <BracketSide team={m.p2_team} user={m.p2_user} winner={m.winner_id === m.p2_id} />
                </div>
                {m.status === "confirmed" || m.status === "forfeited" ? (
                  <div className="text-right">
                    <div className="text-white font-bold tabular-nums">{m.confirmed_score_p1}</div>
                    <div className="text-white font-bold tabular-nums">{m.confirmed_score_p2}</div>
                  </div>
                ) : (
                  <div className="text-[9px] font-mono text-white/40 uppercase">{t("wct.bracket.tbd")}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function BracketSide({ team, user, winner }) {
  return (
    <div className={`flex items-center gap-1.5 ${winner ? "text-emerald-300" : "text-white/80"}`}>
      <span className="text-base">{team?.flag}</span>
      <span className="text-[11px] font-bold truncate">{team?.code}</span>
      <span className="text-[10px] text-white/40 truncate">@{user?.username || ""}</span>
    </div>
  );
}

/* Persistent header showing how many of 48 slots have been claimed. Visible
   in every state (forming / group_stage / knockout) so the player can always
   see overall participation. */
function RegistrationTracker({ t: tn }) {
  const { t } = useT();
  const filled = tn.filled || 0;
  const pct = Math.min(100, (filled / 48) * 100);
  return (
    <div className="rounded-2xl bg-[#12121C] border border-[#1F1F2A] p-3 space-y-2" data-testid="v2-wct-tracker">
      <div className="flex items-baseline justify-between">
        <div className="text-[10px] font-mono uppercase tracking-widest text-[#FFD700]">{t("wct.tracker.title")}</div>
        <div className="text-xs font-mono text-[#71717A]">
          <span className="text-white font-extrabold tabular-nums text-base mr-1" data-testid="v2-wct-tracker-count">{filled}</span>{t("wct.tracker.slots")}
        </div>
      </div>
      <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
        <div className="h-full bg-gradient-to-r from-[#FFD700] to-[#FFB000]" style={{ width: `${pct}%` }} />
      </div>
      <div className="text-[10px] text-[#71717A] font-mono">
        {tn.status === "forming"
          ? t("wct.tracker.remaining", { n: 48 - filled })
          : tn.status === "group_stage"
            ? t("wct.tracker.groupProgress")
            : tn.status === "knockout"
              ? t("wct.tracker.koProgress")
              : t("wct.tracker.complete")}
      </div>
    </div>
  );
}

function GroupsPreview({ t: tn, playersMeta, myId, myGroup }) {
  const { t } = useT();
  const LETTERS = ["A","B","C","D","E","F","G","H","I","J","K","L"];
  const byGroup = useMemo(() => {
    const out = Object.fromEntries(LETTERS.map(g => [g, []]));
    const teamBy = tn.team_by_user || {};
    const groupBy = tn.group_by_user || {};
    for (const [uid, g] of Object.entries(groupBy)) {
      if (!out[g]) continue;
      out[g].push({ uid, team: teamBy[uid] });
    }
    return out;
  }, [tn]);

  return (
    <div className="space-y-2" data-testid="v2-wct-groups-preview">
      <div className="flex items-center justify-between px-1">
        <div className="text-[10px] font-mono uppercase tracking-widest text-[#FFD700]">{t("wct.preview.kicker")}</div>
        <div className="text-[9px] font-mono uppercase tracking-widest text-[#71717A]">{t("wct.preview.meta")}</div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {LETTERS.map(letter => {
          const rows = byGroup[letter] || [];
          const isMyGroup = myGroup === letter;
          return (
            <div
              key={letter}
              data-testid={`v2-wct-preview-group-${letter}`}
              className={`rounded-2xl bg-[#12121C] border overflow-hidden ${isMyGroup ? "border-[#FFD700]/60 shadow-[0_0_0_1px_rgba(255,215,0,0.2)]" : "border-[#1F1F2A]"}`}
            >
              <div className={`px-3 py-1.5 text-[10px] font-mono uppercase tracking-widest border-b flex items-center justify-between ${isMyGroup ? "text-[#FFD700] border-[#FFD700]/30 bg-[#FFD700]/5" : "text-[#FFD700] border-[#1F1F2A]"}`}>
                <span>{t("wct.group", { g: letter })}</span>
                <span className="text-[#71717A]">{rows.length}/4</span>
              </div>
              <div className="divide-y divide-white/5">
                {Array.from({ length: 4 }).map((_, i) => {
                  const row = rows[i];
                  if (!row) {
                    return (
                      <div key={`empty-${i}`} className="flex items-center gap-2 px-3 py-1.5" data-testid={`v2-wct-preview-${letter}-slot-${i}-empty`}>
                        <div className="text-base opacity-30">🏳️</div>
                        <div className="text-[11px] text-[#52525B] italic">{t("wct.preview.openSlot")}</div>
                      </div>
                    );
                  }
                  const meta = playersMeta[row.uid] || {};
                  const isMe = row.uid === myId;
                  return (
                    <div
                      key={row.uid}
                      data-testid={`v2-wct-preview-${letter}-player-${row.uid}`}
                      className={`flex items-center gap-2 px-3 py-1.5 ${isMe ? "bg-[#FFD700]/10" : ""}`}
                    >
                      <div className="text-base">{row.team?.flag || "🏳️"}</div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[11px] font-bold text-white truncate">
                          @{meta.username || row.uid.slice(0,6)}{isMe && <span className="ml-1 text-[8px] font-mono uppercase tracking-widest text-[#FFD700]">{t("wct.you")}</span>}
                        </div>
                        <div className="text-[9px] font-mono text-[#71717A] truncate">{row.team?.code || ""} · {row.team?.name || ""}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
